import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as api from '../lib/api';
import { adoptAccountConsent } from '../lib/consent';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load auth state from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = api.getToken();
      if (token) {
        // Try to get cached user first for faster load
        const cached = api.getCachedUser();
        if (cached) {
          setUser(cached);
        }
        // Then verify with server
        try {
          const userData = await api.getMe();
          setUser(userData);
          // Only a verified payload may influence analytics, never the cache.
          adoptAccountConsent(userData.analyticsConsent);
        } catch (err) {
          // Token invalid, clear it
          api.clearToken();
          setUser(null);
        }
      }
      setIsLoading(false);
    };
    loadUser();
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { user: userData } = await api.login({ email, password });
    setUser(userData);
    adoptAccountConsent(userData.analyticsConsent);
    return userData;
  }, []);

  const signUp = useCallback(async (email, password, name, inviteCode) => {
    const { user: userData } = await api.signup({ email, password, name, inviteCode });
    setUser(userData);
    return userData;
  }, []);

  const signOut = useCallback(() => {
    api.logout();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      return userData;
    } catch {
      return null;
    }
  }, []);

  // One-time announcement: the press is recorded on the account, and the
  // refreshed payload is what clears it locally.
  const acknowledgeAnnouncement = useCallback(async (announcementId) => {
    const userData = await api.acknowledgeAnnouncement(announcementId);
    setUser(userData);
    return userData;
  }, []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoading, 
      signIn, 
      signUp, 
      signOut,
      refreshUser,
      acknowledgeAnnouncement
    }}>
      {children}
    </AuthContext.Provider>
  );
};
